import { useProductsSearched, useTrackEvent } from "@outschool/ui-analytics";
import { useServerExperimentContext } from "@outschool/ui-experiments";
import { useIsMobile } from "@outschool/ui-utils";
import _ from "lodash";
import { useEffect, useRef } from "react";
import { useHistory } from "react-router";

import { useSearchFilters } from "../../lib/useSearchFilters";

type ClassTrackSearchHookParams = {
  loading: boolean;
  exactListings: any[];
  itemsPerPage: number;
  currentUserHasLoaded: boolean;
  exactTotal: number;
  searchUid?: string;
};

type ClassTrackEventType =
  | "is_new_search"
  | "is_filter_change"
  | "is_browser_back";

export default function useClassTrackSearch({
  loading,
  exactListings,
  itemsPerPage,
  currentUserHasLoaded,
  exactTotal,
  searchUid,
}: ClassTrackSearchHookParams) {
  const { filters } = useSearchFilters();
  const trackEvent = useTrackEvent();
  const trackProductsSearched = useProductsSearched();
  const hasTrackedSearchRef = useRef(false);
  const oldFiltersRef = useRef(filters);
  const eventTypeRef = useRef<ClassTrackEventType>("is_new_search");
  const { loggedOutUserExperimentUid } = useServerExperimentContext();

  const history = useHistory();
  const mobileSearch = useIsMobile();

  useEffect(() => {
    if (!_.isEqual(filters, oldFiltersRef.current)) {
      oldFiltersRef.current = filters;
      hasTrackedSearchRef.current = false;
      eventTypeRef.current = "is_filter_change";
    }

    if (
      currentUserHasLoaded &&
      !loading &&
      exactListings &&
      !hasTrackedSearchRef.current
    ) {
      hasTrackedSearchRef.current = true;
      const resultsWithSectionCount = exactListings.filter(
        ({ sectionCount }) => sectionCount > 0,
      ).length;

      trackEvent("Class Search", {
        searchFilters: filters,
        experiments: {
          mobileSearch,
        },
        resultsTotal: exactTotal,
        resultsCount: exactListings.length,
        resultsWithSectionCount,
        resultsLimit: itemsPerPage,
        eventType:
          history.action === "POP" ? "is_browser_back" : eventTypeRef.current,
        searchUid,
        loggedOutUserExperimentUid,
      });

      // Reset event type to default after we emit for a filter change
      if (eventTypeRef.current === "is_filter_change") {
        eventTypeRef.current = "is_new_search";
      }

      if (!!filters?.q) {
        trackProductsSearched({ query: filters.q });
      }
    }
  }, [
    currentUserHasLoaded,
    exactListings,
    filters,
    history,
    itemsPerPage,
    loading,
    loggedOutUserExperimentUid,
    mobileSearch,
    exactTotal,
    searchUid,
    trackEvent,
    trackProductsSearched,
  ]);

  return { searchUid };
}
